import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import TopNav from "../landing/TopNav";
import Footer from "../landing/Footer";

export default function LegalLayout({
    eyebrow,
    title,
    updatedAt,
    siblingLabel,
    siblingTo,
    testIdRoot = "legal",
    children,
}) {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    return (
        <div
            className="relative min-h-screen bg-paper text-ink"
            data-testid={`${testIdRoot}-page`}
        >
            <TopNav variant="legal" />

            <section
                data-testid={`${testIdRoot}-hero`}
                className="relative overflow-hidden border-b border-brand"
            >
                <div className="absolute inset-0 hero-wash pointer-events-none" />
                <div className="noise-overlay" />
                <div className="relative mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-14 sm:pb-20">
                    <Link
                        to="/"
                        data-testid={`${testIdRoot}-back-home`}
                        className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--brand-muted-2)] hover:text-[var(--brand-ink)] transition-colors"
                    >
                        <ArrowLeft size={14} /> Back to home
                    </Link>
                    <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-brand bg-white/70 backdrop-blur px-3 py-1.5">
                        <span className="h-1.5 w-1.5 rounded-full bg-[var(--brand-teal-2)]" />
                        <span className="text-[11.5px] font-semibold tracking-[0.14em] uppercase text-[var(--brand-muted-2)]">
                            {eyebrow}
                        </span>
                    </div>
                    <h1
                        data-testid={`${testIdRoot}-title`}
                        className="mt-5 font-display text-[36px] sm:text-[46px] lg:text-[56px] leading-[1.05] tracking-[-0.02em] text-[var(--brand-ink)]"
                    >
                        {title}
                    </h1>
                    <p
                        data-testid={`${testIdRoot}-updated`}
                        className="mt-3 text-[13.5px] text-[var(--brand-muted)]"
                    >
                        Last updated: <span className="font-medium text-[var(--brand-muted-2)]">{updatedAt}</span>
                    </p>
                    <p className="mt-5 max-w-[62ch] text-[15.5px] leading-relaxed text-[var(--brand-muted-2)]">
                        BetweenUs is offered by{" "}
                        <strong className="font-semibold text-[var(--brand-ink)]">
                            Tylegn Gaming and Entertainment, LLC
                        </strong>
                        . Please read this carefully — it covers how the
                        App works, what we expect from each other, and how
                        we handle your information.
                    </p>
                    {siblingTo ? (
                        <Link
                            to={siblingTo}
                            data-testid={`${testIdRoot}-sibling-link`}
                            className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-white border border-brand px-4 py-2 text-[13px] font-medium text-[var(--brand-ink)] hover:bg-[var(--brand-paper-2)] hover:border-[var(--brand-border-strong)] transition-colors"
                        >
                            Read the {siblingLabel}
                            <ArrowRight size={14} />
                        </Link>
                    ) : null}
                </div>
            </section>

            <article
                className="mx-auto w-full max-w-3xl px-4 sm:px-6 lg:px-8 py-14 sm:py-20 legal-prose"
                data-testid={`${testIdRoot}-content`}
            >
                {children}
            </article>

            <Footer />
        </div>
    );
}
